// src/modules/units/tyres/views/reports/tyrereports.js
//
// Fleet report orchestrator. Reports need the whole fleet, not one page, so it
// pages through GET /api/tyre (stopping on a short page — robust whichever way
// `count` is defined), computes the aggregates, and renders. A header offers CSV
// export (built in-browser) and a manual refresh.
//
// Scope: the selected yard by default, or ONE VEHICLE picked in the filter bar —
// a Unit (gateway), a Truck, a Truck with its hitched trailers, or a Trailer (the
// vocabulary lives in shared/tyre-vehicle-filter.js). As in the register, a vehicle
// is itself a location, so picking one DROPS the yard filter: the report covers that
// vehicle's fitted tyres wherever each was booked in. The vehicle LISTS are still
// scoped to the selected yard's customer sub-tree, like the register's Unit list.

import { fetchAllTyres as fetchFleet } from '../tyres.api.js';
import { fetchUnits, fetchTrucks, fetchTrailers } from '../units.api.js';
import { esc, formatCount } from '../../shared/tyre-format.js';
import { computeFleetReport, tyresToCsv } from './tyre-reports-data.js';
import { reportHTML, reportsLoadingHTML, reportsErrorHTML } from './tyrereports.view.js';
import { downloadCsv } from '../../shared/tyre-csv.js';
import { getSelectedYard, getSelectedYardId } from '../../shared/tyre-scope.js';
import { onTyreEvent, TYRE_EVENTS } from '../../shared/tyre-events.js';
import {
  VEHICLE_KIND,
  VEHICLE_KIND_OPTIONS,
  vehicleKindMeta,
  vehicleOptions,
  vehicleMatcher,
  listsForKind
} from '../../shared/tyre-vehicle-filter.js';

const LIST_FETCHERS = { units: fetchUnits, trucks: fetchTrucks, trailers: fetchTrailers };

// Thin adapter over the module's one pager, keeping the { tyres, capped } /
// { error } shape the call sites below expect.
async function fetchAllTyres(params) {
  const res = await fetchFleet(params);
  if (!res.success) return { error: res.message || 'Could not load tyres.' };
  return { tyres: res.data?.results || [], capped: !!res.data?.capped };
}

export function renderTyreReports(viewEl, ctx = {}) {
  let disposed = false;
  let tyres = [];

  // The vehicle pick. Kind defaults to Unit, matching the register's filter.
  let kind = VEHICLE_KIND.UNIT;
  let vehicleId = '';

  // Vehicle lists for the current customer scope: { units, trucks, trailers }, each
  // an array once loaded or null if its fetch failed. Dropped on a yard change.
  let lists = {};
  let listSeq = 0;
  let loadSeq = 0;

  // The whole-account sweep behind Truck / Trailer picks (GET /api/tyre can only
  // filter by imeiNumber, not unitId), reused while switching between vehicles and
  // dropped on Refresh.
  let accountSweep = null;

  const kindOptions = VEHICLE_KIND_OPTIONS.map((o) =>
    `<option value="${esc(o.value)}"${o.value === kind ? ' selected' : ''}>${esc(o.label)}</option>`).join('');

  const tpl = document.createElement('template');
  tpl.innerHTML = `
    <div class="tm-reports-wrap">
      <div class="tm-reports-head">
        <div class="tm-reports-heading">
          <h2 class="tm-reports-title">Fleet report</h2>
          <p class="tm-reports-sub" data-sub>Gathering fleet data…</p>
        </div>
        <div class="tm-reports-actions">
          <button type="button" class="tm-btn-secondary" data-refresh disabled>Refresh</button>
          <button type="button" class="tm-btn-primary" data-export disabled>Export CSV</button>
        </div>
      </div>
      <div class="tm-toolbar tm-reports-filters">
        <select class="tm-select" data-kind aria-label="Filter by vehicle type">${kindOptions}</select>
        <select class="tm-select tm-select-unit" data-vehicle aria-label="Filter by vehicle" disabled>
          <option value="">${esc(vehicleKindMeta(kind).placeholder)}</option>
        </select>
        <button type="button" class="tm-btn-secondary" data-clear-vehicle hidden>Clear</button>
      </div>
      <div data-body>${reportsLoadingHTML()}</div>
    </div>`;
  const root = tpl.content.firstElementChild;
  const bodyEl = root.querySelector('[data-body]');
  const subEl = root.querySelector('[data-sub]');
  const refreshBtn = root.querySelector('[data-refresh]');
  const exportBtn = root.querySelector('[data-export]');
  const kindSel = root.querySelector('[data-kind]');
  const vehicleSel = root.querySelector('[data-vehicle]');
  const clearBtn = root.querySelector('[data-clear-vehicle]');

  viewEl.replaceChildren(root);

  function setActionsEnabled(on) {
    refreshBtn.disabled = !on;
    exportBtn.disabled = !on || tyres.length === 0;
  }

  // ---- vehicle lists / options ----

  function listsReady(k) {
    return listsForKind(k).every((name) => Array.isArray(lists[name]));
  }

  function currentOptions() {
    return listsReady(kind) ? vehicleOptions(kind, lists) : null;
  }

  function paintVehicleOptions() {
    const meta = vehicleKindMeta(kind);
    const opts = currentOptions();
    const failed = listsForKind(kind).some((name) => lists[name] === null);

    if (!opts) {
      vehicleSel.innerHTML = `<option value="">${failed ? `${esc(meta.noun)}s unavailable` : 'Loading…'}</option>`;
      vehicleSel.disabled = true;
      vehicleSel.title = failed ? `${meta.noun}s unavailable` : '';
      return;
    }
    if (vehicleId && !opts.some((o) => o.id === vehicleId)) vehicleId = '';
    vehicleSel.innerHTML = [`<option value="">${esc(meta.placeholder)}</option>`]
      .concat(opts.map((o) => `<option value="${esc(o.id)}">${esc(o.label)}</option>`))
      .join('');
    vehicleSel.value = vehicleId;
    vehicleSel.disabled = opts.length === 0;
    if (opts.length === 0) vehicleSel.title = 'No vehicles in this scope';
    else vehicleSel.removeAttribute('title');
    clearBtn.hidden = !vehicleId;
  }

  // Fetch whichever lists the current kind still needs, for the current yard's
  // customer (undefined = the signed-in account, at All-yards). A failed list is
  // stored as null so the select says "unavailable" rather than looking empty; it
  // is retried on the next yard change.
  async function loadVehicleLists() {
    paintVehicleOptions();
    const missing = listsForKind(kind).filter((name) => lists[name] === undefined);
    if (!missing.length) return;

    const mySeq = ++listSeq;
    const customerId = getSelectedYard()?.customerId || undefined;
    const results = await Promise.all(missing.map((name) => LIST_FETCHERS[name]({ customerId })));
    if (disposed || mySeq !== listSeq) return;

    missing.forEach((name, i) => {
      lists[name] = results[i].success ? (results[i].data?.results || []) : null;
    });
    paintVehicleOptions();
  }

  // ---- report ----

  function scopeLabel() {
    if (vehicleId) {
      const opt = (currentOptions() || []).find((o) => o.id === vehicleId);
      return `${vehicleKindMeta(kind).noun}: ${opt ? opt.label : vehicleId}`;
    }
    return getSelectedYard()?.name || '';
  }

  async function fetchScopedTyres() {
    if (!vehicleId) return fetchAllTyres({ yardId: getSelectedYardId() });
    if (kind === VEHICLE_KIND.UNIT) return fetchAllTyres({ imeiNumber: vehicleId });

    if (!accountSweep) {
      const res = await fetchAllTyres({});
      if (res.error) return res;
      accountSweep = res;
    }
    const match = vehicleMatcher(kind, vehicleId, lists);
    return { tyres: accountSweep.tyres.filter(match), capped: accountSweep.capped };
  }

  async function load() {
    const mySeq = ++loadSeq;
    setActionsEnabled(false);
    subEl.textContent = 'Gathering fleet data…';
    bodyEl.innerHTML = reportsLoadingHTML();

    const res = await fetchScopedTyres();
    if (disposed || mySeq !== loadSeq) return;       // a newer pick won the race

    if (res.error) {
      tyres = [];
      subEl.textContent = 'Could not load';
      bodyEl.innerHTML = reportsErrorHTML(res.error);
      const retry = bodyEl.querySelector('[data-retry]');
      if (retry) retry.addEventListener('click', load);
      refreshBtn.disabled = false;
      return;
    }

    tyres = res.tyres;
    const report = computeFleetReport(tyres);
    bodyEl.innerHTML = reportHTML(report);
    const scope = scopeLabel();
    subEl.textContent = `${formatCount(tyres.length)} tyres${res.capped ? ` (first ${formatCount(tyres.length)}, capped)` : ''}${scope ? ` · ${scope}` : ''}`;
    setActionsEnabled(true);
  }

  // ---- events ----

  kindSel.addEventListener('change', () => {
    kind = kindSel.value;
    const hadVehicle = !!vehicleId;
    vehicleId = '';
    clearBtn.hidden = true;
    loadVehicleLists();
    if (hadVehicle) load();                          // back to the yard-wide report
  });

  vehicleSel.addEventListener('change', () => {
    vehicleId = vehicleSel.value;
    clearBtn.hidden = !vehicleId;
    load();
  });

  clearBtn.addEventListener('click', () => {
    vehicleId = '';
    vehicleSel.value = '';
    clearBtn.hidden = true;
    load();
  });

  refreshBtn.addEventListener('click', () => {
    if (refreshBtn.disabled) return;
    accountSweep = null;
    load();
  });

  exportBtn.addEventListener('click', () => {
    if (exportBtn.disabled || !tyres.length) return;
    const stamp = new Date().toISOString().slice(0, 10);
    const slug = vehicleId
      ? `-${String(scopeLabel()).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`
      : '';
    downloadCsv(`tyre-fleet-report${slug}-${stamp}.csv`, tyresToCsv(tyres));
  });

  // A yard change re-scopes the vehicle lists (the yard's customer sub-tree), so
  // the pick is cleared — as in the register — and the report recomputes.
  const offScope = onTyreEvent(TYRE_EVENTS.SCOPE_CHANGED, () => {
    vehicleId = '';
    lists = {};
    loadVehicleLists();
    load();
  });

  // A tyre write elsewhere can move tyres between vehicles.
  const offChanged = onTyreEvent(TYRE_EVENTS.CHANGED, () => { accountSweep = null; });

  loadVehicleLists();
  load();

  return () => { disposed = true; offScope(); offChanged(); };
}
